import { observer } from "mobx-react";
import Modal from "./model";
import React, { useImperativeHandle, useState } from "react";
import { useStyles } from "../components/app-container-components/configure-input";
import t from "../stores/language";
import { Button, Container, Flex, Group, Text } from "@mantine/core";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { Icon } from "./icons";
import { faImage } from "@fortawesome/pro-duotone-svg-icons";
import theme from "../stores/theme";
import { FileRejection } from "react-dropzone";
import { delay } from "../components/app-container-components/module-assumptions";

export interface ImageUploadHandle {
  show: () => void;
}
export interface ImageUploadProps {
  title: string;
  onUpload: (file: File) => Promise<boolean>;
}

const ImageUpload = observer(
  React.forwardRef<ImageUploadHandle, ImageUploadProps>(function ImageUpload(props, ref) {
    const { classes } = useStyles();
    const [selectedFile, setFile] = useState<File | null>(null);
    const [visible, setVisible] = useState<boolean>(false);
    const [error, setError] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });

    useImperativeHandle(ref, () => {
      return {
        show: show,
      };
    });

    const show = () => {
      setVisible(true);
    };
    const resetState = () => {
      setVisible(false);
      if (selectedFile != null) setFile(null);
      setError({ isError: false, message: "" });
    };
    const uploadImage = async (file: File) => {
      if (error.isError) {
        return;
      }
      const result = await props.onUpload(file);
      if (result) {
        setError({ isError: false, message: t("Uploaded sucessfully") });
        await delay(300);
      } else {
        setError({ isError: true, message: t("Upload failed") });
        return;
      }
      resetState();
    };
    return (
      <Modal
        opened={visible}
        header={props.title}
        footer={
          <Group className={classes.modalFooter}>
            <Button
              variant={"default"}
              onClick={() => {
                resetState();
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={() => {
                if (selectedFile == null) {
                  setError({ isError: true, message: "Please Select File" });
                  return;
                }
                uploadImage(selectedFile);
              }}
            >
              {t("Upload")}
            </Button>
          </Group>
        }
        withCloseButton={true}
        onClose={() => {
          resetState();
        }}
        size={"35%"}
      >
        <Container p={15}>
          <Dropzone
            accept={["image/png"]}
            onDrop={(files: FileWithPath[]) => {
              if (error.isError) setError({ isError: false, message: "" });
              setFile(files[0]);
            }}
            onReject={(fileRejections: FileRejection[]) => {
              setError({ isError: true, message: fileRejections[0].errors[0].message });
            }}
          >
            <Flex align={"center"} direction={"column"} style={{ pointerEvents: "none" }}>
              <Dropzone.Idle>
                <Icon size="2x" color={theme.other?.arc.colors[1]} icon={faImage} />
              </Dropzone.Idle>
              <Text>{"Drag image here or click to select file"}</Text>
              <Text size={"sm"} color="dimmed">
                {selectedFile ? selectedFile.name : "accepts: image/png"}
              </Text>
            </Flex>
          </Dropzone>
          {error.message !== "" ? (
            <Text size={theme.fontSizes?.sm} color={error.isError ? "red" : "green"} pt={theme.other?.arc.paddings[2]}>
              {error.message}
            </Text>
          ) : null}
        </Container>
      </Modal>
    );
  })
);
export default ImageUpload;
